import type { ComputedRef } from 'vue'
import { computed } from 'vue'

import { usePlanStore } from './usePlanStore'
import { LimitType, Resource } from './planTypes'
import { getResourceLimit, getResourceUsage } from './limitAndUsage'
import { useBillingStore } from './useBillingStore'

/**
 * Intended to be the single source of truth around usage and limits for
 * the 'annotation_credit' resource
 *
 * Should be extended with usage checks, limits and any other logic that might be
 * needed across the app.
 */
export const useResourceAnnotationCredit = (): {
  isCreditAtLimit: ComputedRef<boolean>
  remainingCredits: ComputedRef<number>
  limitType: ComputedRef<LimitType>
} => {
  const planStore = usePlanStore()
  const billingStore = useBillingStore()

  const usage = computed(() => {
    if (planStore.customerPlan) {
      return getResourceUsage(planStore.customerPlan, Resource.ANNOTATION_CREDIT)
    }

    if (billingStore.billingInfo && billingStore.billingInfo.customer_subscription) {
      return billingStore.billingInfo.customer_subscription.annotation_credits_used
    }

    return 0
  })

  const limit = computed(() => {
    if (planStore.customerPlan) {
      const license = planStore.customerPlan?.plan_licenses?.find(
        (l) => l.resource === Resource.ANNOTATION_CREDIT,
      )
      return license
        ? getResourceLimit(planStore.customerPlan, Resource.ANNOTATION_CREDIT)
        : Infinity
    }

    if (!planStore.hasCustomerPlanLoaded) {
      // plan might not have loaded yet, meaning we don't know if the customer is LBB or CBB
      return Infinity
    }

    if (billingStore.billingInfo?.customer_subscription) {
      return (
        billingStore.billingInfo.customer_subscription.annotation_credits_standard_max_in_period +
        billingStore.billingInfo.customer_subscription.annotation_credits_bonus
      )
    }

    return Infinity
  })

  const isCreditAtLimit = computed(() => usage.value >= limit.value)
  const remainingCredits = computed(() => Math.max(limit.value - usage.value, 0))

  const limitType = computed<LimitType>(() => {
    if (planStore.customerPlan) {
      const license = planStore.customerPlan.plan_licenses.find(
        (l) => l.resource === Resource.ANNOTATION_CREDIT,
      )
      if (!license) {
        return LimitType.NONE
      }
      return license.is_blocking ? LimitType.HARD : LimitType.SOFT
    }

    if (!planStore.hasCustomerPlanLoaded) {
      // plan might not have loaded yet, meaning we don't know if the customer is LBB or CBB
      // so we default to soft just in case
      return LimitType.SOFT
    }

    return LimitType.HARD
  })

  return { isCreditAtLimit, remainingCredits, limitType }
}
